/**
 * Mixin for metadata components that need to update documentMetadata.value
 *
 * This mixin provides a clean API for updating metadata values by emitting events
 * instead of directly mutating props, following Vue best practices.
 *
 * The parent component (DocumentMetadata.vue) handles these events and performs
 * the actual mutation at that level, keeping the mutation close to the data source.
 *
 * Usage in a metadata component:
 *
 * import metadataMixin from '@/mixins/metadataMixin';
 *
 * export default {
 *   mixins: [metadataMixin],
 *   // ... rest of component
 * }
 *
 * Then use `localValue` in computed or v-model
 */
export default {
  props: {
    documentMetadata: Object,
  },

  computed: {
    /**
     * Computed property for two-way binding to documentMetadata.value
     * Emits 'update:value' event instead of directly mutating the prop
     */
    localValue: {
      get() {
        return this.documentMetadata.value;
      },
      set(value) {
        // Emit event for parent to handle the update
        this.$emit("update:value", value);
      },
    },
  },

  methods: {
    /**
     * Updates a property on documentMetadata by emitting an event
     * @param {string} property - The property name to update
     * @param {*} value - The new value
     */
    updateMetadataProperty(property, value) {
      this.$emit("update:property", { property, value });
    },

    /**
     * Clears the highlight after a timeout (common pattern in metadata components)
     * Uses updateMetadataProperty to emit event instead of mutating directly
     */
    clearHighlightAfterDelay() {
      if (this.documentMetadata.highlight) {
        this.highlight = true;
        setTimeout(() => {
          this.highlight = false;
          this.updateMetadataProperty("autoExtracted", false);
        }, 2000);
      }
    },
  },
};
